import { useRef } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { replaceSoilSightCropYears } from "services";

import { useIsAuthenticated } from "utils";

import { GET_SOIL_SIGHT_SCENARIO, REPLACE_SOIL_SIGHT_CROP_YEARS } from "consts";

import { ReplaceCropYearsBody, ReplaceCropYearsResponse, SoilSightScenarioDetail } from "models";

type MutateArgs = {
  scenarioId: string;
  body: ReplaceCropYearsBody;
  requestVersion?: number;
};

type WaitingReplace = { body: ReplaceCropYearsBody; sent?: Promise<ReplaceCropYearsResponse> };

/** A scenario's last PUT, and the one waiting behind it (if any), which takes the newest body until it starts. */
type ScenarioReplaceQueue = { tail: Promise<ReplaceCropYearsResponse>; waiting: WaitingReplace | null };

// The API reads a scenario's rows and then writes them without a lock, so overlapping PUTs for one scenario can duplicate a year.
const replaceQueueByScenario = new Map<string, ScenarioReplaceQueue>();

/** Sends one scenario's crop-year PUTs one after another; a PUT still waiting its turn sends the newest body, and its callers share the result. */
const replaceInOrder = (scenarioId: string, body: ReplaceCropYearsBody): Promise<ReplaceCropYearsResponse> => {
  const queue = replaceQueueByScenario.get(scenarioId);
  const forgetWhenSettled = (entry: ScenarioReplaceQueue, sent: Promise<ReplaceCropYearsResponse>) => {
    const forget = () => {
      if (entry.tail === sent) replaceQueueByScenario.delete(scenarioId);
    };
    sent.then(forget, forget);
  };

  if (!queue) {
    const sent = Promise.resolve(replaceSoilSightCropYears(scenarioId, body));
    const entry: ScenarioReplaceQueue = { tail: sent, waiting: null };
    replaceQueueByScenario.set(scenarioId, entry);
    forgetWhenSettled(entry, sent);
    return sent;
  }

  // Each body is the full rotation, so a newer one replaces a waiting one rather than queueing behind it.
  if (queue.waiting?.sent) {
    queue.waiting.body = body;
    return queue.waiting.sent;
  }

  const waiting: WaitingReplace = { body };
  const sent = queue.tail
    .catch(() => undefined)
    .then(() => {
      if (queue.waiting === waiting) queue.waiting = null;
      return replaceSoilSightCropYears(scenarioId, waiting.body);
    });
  waiting.sent = sent;
  queue.waiting = waiting;
  queue.tail = sent;
  forgetWhenSettled(queue, sent);
  return sent;
};

export const useReplaceSoilSightCropYears = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenticated();
  const latestAppliedVersionRef = useRef<Map<string, number>>(new Map());

  return useMutation({
    mutationKey: [REPLACE_SOIL_SIGHT_CROP_YEARS],
    mutationFn: ({ scenarioId, body }: MutateArgs) => {
      if (!isAuthenticated) return Promise.reject(new Error("Not Authorized"));
      return replaceInOrder(scenarioId, body);
    },
    // Patch the cache with the variables we just sent rather than the server
    // response — the user is the source of truth and may have typed again
    // between dispatch and resolution. We also intentionally avoid
    // invalidateQueries here so any in-flight detail subscriber doesn't
    // refetch and remount mid-edit (which is the flicker we're avoiding).
    onSuccess: (_data, vars) => {
      if (vars.requestVersion !== undefined) {
        const latestApplied = latestAppliedVersionRef.current.get(vars.scenarioId) ?? 0;
        if (vars.requestVersion < latestApplied) return;
        latestAppliedVersionRef.current.set(vars.scenarioId, vars.requestVersion);
      }

      queryClient.setQueryData<SoilSightScenarioDetail | undefined>([GET_SOIL_SIGHT_SCENARIO, vars.scenarioId], (old) =>
        old ? { ...old, cropYears: vars.body.cropYears } : old
      );
    },
    // The page copies unsaved edits into the detail cache (syncScenarioDetailCache); mark it stale so it is refetched before reuse.
    onError: (_error, vars) => {
      queryClient.invalidateQueries({ queryKey: [GET_SOIL_SIGHT_SCENARIO, vars.scenarioId], refetchType: "none" });
    },
  });
};
